/**
 * GOOP ZONE — WildCardMachine System
 * ========================
 * Design Philosophy: Industrial dungeon aesthetic, dark grunge.
 *
 * GOOP = Gear Off Other Players.
 * Chests are salvage crates scattered across the map — one guaranteed per room,
 * plus additional chests in open ground between clusters.
 *
 * Two chest types:
 *   GOOP WildCardMachine (regular): Costs gold to open. Drops 1 randomized gear buff (stackable).
 *   Gold GOOP WildCardMachine (free): Always free. Drops 1 randomized gear buff (higher rarity).
 *
 * Each type has left-facing and right-facing variants (horizontal mirror).
 * The map generator assigns facing based on room position / RNG.
 *
 * States:
 *   closed  → Player approaches, prompt appears "Open? [cost]g"
 *   open    → WildCardMachine opened, loot visible, buff selection UI triggered
 *   gone    → After buff chosen, chest fades out and is removed from world
 *
 * Integration note for main game engine:
 *   - Call updateChests(chests, playerPos) every frame to detect proximity
 *   - When chest.state === 'open', show buff selection UI
 *   - After buff chosen, call markMachineGone(chest)
 *   - WildCardMachine disappears from world (no sprite rendered when state === 'gone')
 */

import {
  BORDER_COLLISION_NORTH, BORDER_COLLISION_SOUTH,
  BORDER_COLLISION_WEST,  BORDER_COLLISION_EAST,
} from './borderWalls';

// ─── Asset URLs ───────────────────────────────────────────────────────────────

// BETA 22.89 — CHESTS ARE GONE (Dylan: "remove the chest. they are no use to
// me anymore. those are the wild card machines where you have to spend tokens
// to purchase wildcards"). Every chest entity is now a WILD CARD MACHINE:
// same placement/proximity/cost skeleton, vending-machine sprite, token cost.
// BETA 23.12 — Dylan's TWO unique angled machines (not mirrors — different
// art, different dimensions): left-facing + right-facing, chosen by the
// chest's seeded `facing`. The blank top sign is where the engine bakes the
// glowing token cost (quads measured per sprite in engine._MACHINE_SIGN_QUADS
// — remeasure if these files are ever replaced). Cache rule: any pixel change
// to these files = NEW filename (v4, v5, …).
export const MACHINE_ASSETS = {
  goop: {
    closedLeft:  '/wild-cards/machine_left_v3.png',
    closedRight: '/wild-cards/machine_right_v3.png',
    openLeft:    '/wild-cards/machine_left_v3.png',
    openRight:   '/wild-cards/machine_right_v3.png',
  },
  gold: {
    closedLeft:  '/wild-cards/machine_left_v3.png',
    closedRight: '/wild-cards/machine_right_v3.png',
    openLeft:    '/wild-cards/machine_left_v3.png',
    openRight:   '/wild-cards/machine_right_v3.png',
  },
  elite: {
    closedLeft:  '/wild-cards/machine_left_v3.png',
    closedRight: '/wild-cards/machine_right_v3.png',
    openLeft:    '/wild-cards/machine_left_v3.png',
    openRight:   '/wild-cards/machine_right_v3.png',
  },
} as const;

// ─── Constants ────────────────────────────────────────────────────────────────

/** World-unit radius within which the player can interact with a chest */
// BETA 22.95 — 60 → 140: machines draw much larger now AND the cabinet has
// solid player collision, so the player physically can't reach 60wu of the
// center anymore. 140 = comfortably interactable standing beside the machine.
export const MACHINE_INTERACT_RADIUS = 140;

/** Visual size of the chest sprite in world units */
export const MACHINE_SPRITE_SIZE = 80;

/**
 * Gold cost to open a regular GOOP chest.
 * Escalates per chest opened this run (handled by game engine, not this module).
 * Reduced from 30 → 15 so early chests are affordable on low enemy gold drops.
 */
export const MACHINE_BASE_COST = 15;

/**
 * BETA 21.57 — DEPRECATED. World chests are no longer randomly gold; the free
 * Gold GOOP chests now all live inside rooms (generateRoomMachines) and every
 * open-ground chest is paid 'goop'. Kept exported to avoid breaking imports.
 */
export const GOLD_CHEST_RATIO = 0.20;

/** Minimum world-unit distance between any two world chests (open-ground placement) */
export const MACHINE_MIN_SPACING = 600;

// ─── Types ────────────────────────────────────────────────────────────────────

export type MachineType    = 'goop' | 'gold' | 'elite';
export type MachineFacing  = 'left' | 'right';
export type MachineState   = 'closed' | 'open' | 'gone';

/**
 * A single chest instance on the map.
 * Stored in RoomMap.chests[].
 */
export interface WildCardMachine {
  /** Unique ID for this chest instance */
  id: string;
  /** World-space center position */
  x: number;
  y: number;
  /** Regular GOOP (costs gold) or Gold GOOP (free) */
  type: MachineType;
  /** Which direction the chest faces */
  facing: MachineFacing;
  /** Current state */
  state: MachineState;
  /** BETA 22.95 — wall-clock ms of the moment the machine was bought; the
   *  renderer plays a decaying physical SHAKE from this timestamp (Dylan:
   *  "the wild card machine should shake when you buy it"). */
  vendShakeAt?: number;
  /**
   * Whether this chest is inside a room (true) or in open ground (false).
   * Room chests are always 'goop' type. World chests can be 'gold'.
   */
  inRoom: boolean;
  /** Room ID this chest belongs to (if inRoom === true) */
  roomId?: string;
}

// ─── Placement ────────────────────────────────────────────────────────────────

/**
 * Generates ONE chest per room, placed near the center of each room.
 * BETA 21.57 — Room chests are now FREE Gold GOOP chests (was paid 'goop').
 * Dylan: "make all free chests be in rooms" — rooms are the reward for exploring,
 * so the free higher-rarity chest lives inside; the paid chests moved to open
 * ground (see generateWorldMachines). This pulls players into rooms.
 * Facing is randomized per-room using the seeded RNG.
 *
 * @param rooms   Array of placed rooms from generateRoomMap()
 * @param seed    Map seed for deterministic placement
 * @returns       Array of WildCardMachine objects (one per room, state='closed')
 */
export function generateRoomMachines(
  rooms: Array<{ id: string; x: number; y: number; size: number }>,
  seed: number
): WildCardMachine[] {
  let s = (seed ^ 0xBEEFCAFE) >>> 0;
  const rng = () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xFFFFFFFF;
  };

  return rooms.map((room, i) => {
    // Place chest slightly off-center within the room for visual interest
    const offsetX = (rng() - 0.5) * room.size * 0.25;
    const offsetY = (rng() - 0.5) * room.size * 0.25;
    const facing: MachineFacing = rng() < 0.5 ? 'left' : 'right';

    return {
      id: `chest_room_${i}_${seed}`,
      x: room.x + room.size / 2 + offsetX,
      y: room.y + room.size / 2 + offsetY,
      // BETA 22.95 — NO FREE MACHINES (Dylan: "no free machines any more.
      // we need to balance the game so that the tokens feel like you really
      // need to get them"). Every room machine is a paid 'goop' machine now;
      // the only free opens left are elite drops (earned, not found).
      type: 'goop' as MachineType,
      facing,
      state: 'closed' as MachineState,
      inRoom: true,
      roomId: room.id,
    };
  });
}

/**
 * Generates world chests in open ground between clusters.
 * Count equals the number of rooms (doubling the total chest count).
 * BETA 21.57 — ALL world chests are now paid regular GOOP (was 20% free gold).
 * The free chests moved into rooms (see generateRoomMachines); the paid chests
 * that used to sit in rooms now live out on the open floor. So: open ground =
 * spend gold, rooms = free loot.
 *
 * @param seed        Map seed
 * @param worldSize   World size in units
 * @param rooms       All placed rooms (to avoid overlap)
 * @param roomCount   Number of rooms — world chest count matches this
 * @returns           Array of WildCardMachine objects (state='closed')
 */
export function generateWorldMachines(
  seed: number,
  worldSize: number,
  rooms: Array<{ x: number; y: number; size: number }>,
  roomCount: number,
  // BETA 22.99 — POI SEPARATION (fix pack v6 doc 12: a machine spawned ON a
  // shrine platform). Machines must clear every reserved POI footprint.
  avoidPois: Array<{ x: number; y: number; r: number }> = [],
): WildCardMachine[] {
  const chests: WildCardMachine[] = [];

  // Spawn bounds = inside the actual border walls, not a % guess.
  const PAD = 80;
  const xMin = BORDER_COLLISION_WEST + PAD;
  const xMax = worldSize - BORDER_COLLISION_EAST - PAD;
  const yMin = BORDER_COLLISION_NORTH + PAD;
  const yMax = worldSize - BORDER_COLLISION_SOUTH - PAD;

  let s = (seed ^ 0xDEADF00D) >>> 0;
  const rng = () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xFFFFFFFF;
  };

  const targetCount = roomCount;
  let attempts = 0;

  while (chests.length < targetCount && attempts < targetCount * 300) {
    attempts++;
    const cx = xMin + rng() * (xMax - xMin);
    const cy = yMin + rng() * (yMax - yMin);

    // Must not be inside any room footprint
    const inRoom = rooms.some(r => {
      const half = r.size / 2 + 60;
      return Math.abs(cx - r.x - r.size / 2) < half &&
             Math.abs(cy - r.y - r.size / 2) < half;
    });
    if (inRoom) continue;

    // Must be far enough from other world chests
    const tooClose = chests.some(c =>
      Math.hypot(cx - c.x, cy - c.y) < MACHINE_MIN_SPACING
    );
    if (tooClose) continue;

    // BETA 22.99 — must clear every reserved POI footprint (shrines, spawn…)
    const onPoi = avoidPois.some(pz => Math.hypot(cx - pz.x, cy - pz.y) < pz.r);
    if (onPoi) continue;

    // BETA 21.57 — keep one rng() draw so seeded placement stays deterministic
    // for existing seeds, but all world chests are now paid 'goop'.
    rng();
    const facing: MachineFacing = rng() < 0.5 ? 'left' : 'right';

    chests.push({
      id: `chest_world_${chests.length}_${seed}`,
      x: cx,
      y: cy,
      type: 'goop',
      facing,
      state: 'closed',
      inRoom: false,
    });
  }

  return chests;
}

// ─── Runtime ──────────────────────────────────────────────────────────────────

/**
 * Returns the correct sprite URL for a chest based on its type, facing, and state.
 */
export function getMachineSprite(chest: WildCardMachine): string | null {
  if (chest.state === 'gone') return null;
  const assets = MACHINE_ASSETS[chest.type];
  if (chest.state === 'open') {
    return chest.facing === 'left' ? assets.openLeft : assets.openRight;
  }
  return chest.facing === 'left' ? assets.closedLeft : assets.closedRight;
}

/**
 * Opens a chest (transitions closed → open).
 * Call this when the player pays the gold cost (or it's a free gold chest).
 */
export function openMachine(chest: WildCardMachine): void {
  if (chest.state === 'closed') {
    chest.state = 'open';
  }
}

/**
 * Removes a chest from the world after the player has collected the loot.
 * Call this after the buff selection UI closes.
 */
export function markMachineGone(chest: WildCardMachine): void {
  chest.state = 'gone';
}

/**
 * BETA 20.49 — Blue Elite WildCardMachine factory. Spawned at an elite enemy's death
 * position. Free to open, guaranteed-legendary loot. inRoom:false so it isn't
 * treated as a room chest (room chests are always 'goop').
 */
export function createEliteMachine(x: number, y: number): WildCardMachine {
  return {
    id: `elite_chest_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
    x,
    y,
    type: 'elite',
    facing: Math.random() < 0.5 ? 'left' : 'right',
    state: 'closed',
    inRoom: false,
  };
}

/**
 * Returns true if the player is close enough to interact with a chest.
 *
 * BETA 14.53 — Signature changed from (chest, {x, y}) to (chest, px, py) to
 * eliminate the object literal allocation at every call site. Plus replaced
 * Math.hypot with squared-distance compare (no sqrt needed for "near" check).
 * DevTools showed isPlayerNearMachine at 9.4% of total CPU + heavy contributor
 * to Major GC pressure (33% of CPU was GC).
 */
export function isPlayerNearMachine(
  chest: WildCardMachine,
  px: number,
  py: number,
): boolean {
  if (chest.state !== 'closed') return false;
  const dx = px - chest.x;
  const dy = py - chest.y;
  return dx * dx + dy * dy <= MACHINE_INTERACT_RADIUS * MACHINE_INTERACT_RADIUS;
}
